import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { useSavingsGame } from './useSavingsGame';
import { formatCurrency } from '@/lib/utils';
import { Brain, Trophy, Target, BarChart, Award, PiggyBank, Timer, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { GAME_POINTS } from '@/lib/cerebrix-config';
import confetti from 'canvas-confetti';
import { SAVINGS_GOALS, SAVINGS_STRATEGIES } from './types';

export const SavingsGame: React.FC = () => {
  const navigate = useNavigate();
  const { state, startGame, selectGoal, selectStrategy, addSaving, updateProgress } = useSavingsGame();

  // Función para mostrar confetti cuando se completa la meta
  const showConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  };

  // Componente para el resumen final del juego
  const GameSummary = () => {
    // Si es la primera vez que se muestra el resumen y se completó la meta, mostrar confetti
    React.useEffect(() => {
      if (state.stats.goalCompleted) {
        showConfetti();
      }
    }, []);

    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center p-4 bg-white rounded-lg shadow-lg w-full max-w-md mx-auto space-y-4"
      >
        <div className="text-center">
          <h2 className="text-3xl font-bold text-primary mb-1">
            {state.stats.goalCompleted ? '¡META DE AHORRO COMPLETADA! ' : 'TIEMPO AGOTADO '}
          </h2>
          <p className="text-base text-muted-foreground">
            {state.stats.goalCompleted 
              ? `Has ahorrado ${formatCurrency(state.savedAmount)} para tu meta "${state.selectedGoal?.name}"` 
              : 'No has podido completar tu meta de ahorro a tiempo'}
          </p>
        </div>

        {/* Puntuación y Cerebrix */}
        <div className="bg-primary/5 w-full rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <span className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              <span className="font-medium">Puntos</span>
            </span>
            <span className="text-2xl font-bold">{state.score}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-500" />
              <span className="font-medium">Cerebrix</span>
            </span>
            <span className="text-2xl font-bold">{state.cerebrixEarned || 0}</span>
          </div>
        </div>

        {/* Desglose de puntos simplificado y más visual */}
        <div className="w-full bg-primary/5 rounded-lg p-4">
          <h3 className="font-bold text-center text-lg mb-3">
            DESGLOSE DE PUNTOS (MÁXIMO 100)
          </h3>
          
          {/* Explicación de puntos */}
          <div className="bg-blue-50 p-3 rounded-md mb-4 text-sm border-2 border-blue-200">
            <p className="text-center">Para obtener todos los puntos (100), debes cumplir con todos los criterios.</p>
          </div>
          
          <ul className="space-y-3">
            <li className={`flex justify-between items-center p-3 rounded-md ${state.stats.goalCompleted ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>
              <div className="flex items-center gap-2">
                {state.stats.goalCompleted ? 
                  <span className="text-white font-bold text-lg"></span> : 
                  <span className="text-red-600 font-bold text-lg"></span>
                }
                <span className="font-medium"> Meta completada</span>
              </div>
              <span className="font-bold">{state.stats.goalCompleted ? `+${GAME_POINTS.SAVINGS.COMPLETION}` : '0'}</span>
            </li>
            
            <li className={`flex justify-between items-center p-3 rounded-md ${state.stats.hasSmartGoal ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>
              <div className="flex items-center gap-2">
                {state.stats.hasSmartGoal ? 
                  <span className="text-white font-bold text-lg"></span> : 
                  <span className="text-red-600 font-bold text-lg"></span>
                }
                <span className="font-medium"> Meta SMART</span>
              </div>
              <span className="font-bold">{state.stats.hasSmartGoal ? `+${GAME_POINTS.SAVINGS.SMART_GOAL}` : '0'}</span>
            </li>
            
            <li className={`flex justify-between items-center p-3 rounded-md ${state.stats.hasStrategy ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>
              <div className="flex items-center gap-2">
                {state.stats.hasStrategy ? 
                  <span className="text-white font-bold text-lg"></span> : 
                  <span className="text-red-600 font-bold text-lg"></span>
                }
                <span className="font-medium"> Buena estrategia</span>
              </div>
              <span className="font-bold">{state.stats.hasStrategy ? `+${GAME_POINTS.SAVINGS.STRATEGY}` : '0'}</span>
            </li>
            
            <li className={`flex justify-between items-center p-3 rounded-md ${state.stats.consistentSavings ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>
              <div className="flex items-center gap-2">
                {state.stats.consistentSavings ? 
                  <span className="text-white font-bold text-lg"></span> : 
                  <span className="text-red-600 font-bold text-lg"></span>
                }
                <span className="font-medium"> Ahorros consistentes</span>
              </div>
              <span className="font-bold">{state.stats.consistentSavings ? `+${GAME_POINTS.SAVINGS.CONSISTENCY}` : '0'}</span>
            </li>
            
            {!state.stats.consistentSavings && (
              <li className="text-sm bg-amber-100 p-3 rounded-md border-2 border-amber-300">
                <p className="font-bold text-amber-800 mb-1">CONSEJO IMPORTANTE:</p>
                <p className="text-amber-800">Para obtener +15 puntos por consistencia, debes hacer pequeños ahorros regulares en lugar de un solo ahorro grande.</p>
              </li>
            )}
          </ul>
        </div>

        {/* Botones de acción */}
        <div className="flex flex-col sm:flex-row gap-4 w-full">
          <Button
            variant="outline"
            onClick={() => navigate('/games')}
            className="w-full"
          >
            Volver a Juegos
          </Button>
          <Button 
            onClick={startGame}
            className="w-full"
          >
            Jugar de Nuevo
          </Button>
        </div>
      </motion.div>
    );
  };

  const content = !state.gameStarted ? (
    <div className="text-center">
      <div className="space-y-4 mb-6">
        <div className="flex items-center justify-center gap-4">
          <Target className="w-6 h-6 text-primary" />
          <span>Elige una meta SMART</span>
        </div>
        <div className="flex items-center justify-center gap-4">
          <BarChart className="w-6 h-6 text-green-500" />
          <span>Planifica tu estrategia de ahorro</span>
        </div>
        <div className="flex items-center justify-center gap-4">
          <Award className="w-6 h-6 text-yellow-500" />
          <span>Gana Cerebrix por ser constante</span>
        </div>
      </div>
      <Button onClick={startGame} size="lg">¡Comenzar!</Button>
    </div>
  ) : state.isGameOver ? (
    // Mostrar el resumen del juego cuando termina
    <GameSummary />
  ) : (
    <div className="space-y-6">
      {!state.selectedGoal ? (
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Elige tu Meta de Ahorro
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SAVINGS_GOALS.map((goal) => (
              <Button
                key={goal.id}
                onClick={() => selectGoal(goal)}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:shadow-lg hover:border-gray-300 hover:scale-[1.01] transition-all duration-150"
              >
                <span className="text-2xl">{goal.icon}</span>
                <span className="font-semibold">{goal.name}</span>
                <span className="text-sm text-gray-600">{formatCurrency(goal.amount)}</span>
              </Button>
            ))}
          </div>
        </Card>
      ) : !state.selectedStrategy ? (
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart className="w-5 h-5 text-primary" />
            Elige tu Estrategia de Ahorro
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SAVINGS_STRATEGIES.map((strategy) => (
              <Button
                key={strategy.id}
                onClick={() => selectStrategy(strategy)}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2 hover:shadow-lg hover:border-gray-300 hover:scale-[1.01] transition-all duration-150"
              >
                <span className="text-2xl">{strategy.icon}</span>
                <span className="font-semibold">{strategy.name}</span>
                <span className="text-sm text-gray-600">{strategy.description}</span>
              </Button>
            ))}
          </div>
        </Card>
      ) : (
        <>
          <Card className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <h4 className="font-semibold mb-2">Tu Meta</h4>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl">{state.selectedGoal.icon}</span>
                  <span>{state.selectedGoal.name}</span>
                </div>
                <div className="text-sm text-gray-600 mb-4">
                  Meta: {formatCurrency(state.selectedGoal.amount)}
                </div>

                <h4 className="font-semibold mb-2">Tu Estrategia</h4>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl">{state.selectedStrategy.icon}</span>
                  <span>{state.selectedStrategy.name}</span>
                </div>
                <div className="text-sm text-gray-600">
                  {state.selectedStrategy.description}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    Progreso del Ahorro
                  </h4>
                  <Progress
                    value={(state.savedAmount / state.selectedGoal.amount) * 100}
                    className="h-4"
                  />
                  <div className="flex justify-between mt-2 text-sm">
                    <span>Ahorrado: {formatCurrency(state.savedAmount)}</span>
                    <span>Meta: {formatCurrency(state.selectedGoal.amount)}</span>
                  </div>
                </div>

                {/* Sección de consistencia de ahorro */}
                <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 mb-6">
                  <h3 className="text-xl font-bold text-center text-blue-700 mb-3">
                    ¿CÓMO GANAR +15 PUNTOS EXTRA?
                  </h3>
                  
                  <div className="bg-white p-3 rounded mb-4">
                    <p className="text-center">
                      Haz pequeños ahorros regulares en lugar de un solo ahorro grande
                    </p>
                    <p className="text-center font-medium">
                      Necesitas completar al menos 5 de los 6 períodos para ganar puntos por consistencia
                    </p>
                  </div>
                  
                  <div className="mb-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium">Períodos completados:</span>
                      <span className="font-bold">{state.periods.filter(p => p.completed).length}/6</span>
                    </div>
                    
                    <div className="flex justify-between gap-1 mb-2">
                      {Array.from({ length: 6 }).map((_, index) => (
                        <div 
                          key={index} 
                          className={`flex-1 h-12 flex items-center justify-center rounded font-bold ${
                            state.periods[index]?.completed ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {index + 1}
                        </div>
                      ))}
                    </div>
                    
                    {/* Indicador de tiempo para el próximo período */}
                    <div className="bg-gray-100 p-2 rounded text-center text-sm">
                      <span className="font-medium">
                        {state.timeLeft > 0 ? (
                          <>
                            Período actual: {Math.min(
                              Math.floor((300 - state.timeLeft) / (300 / 6)) + 1, 
                              6
                            )} de 6
                            <br />
                            {state.timeLeft > 0 && state.timeLeft < 300 && (
                              <>
                                Siguiente período en: {Math.ceil((300 / 6) - ((300 - state.timeLeft) % (300 / 6)))} segundos
                              </>
                            )}
                          </>
                        ) : (
                          "Tiempo finalizado"
                        )}
                      </span>
                    </div>
                  </div>
                  
                  <div className={`p-3 rounded-md text-white text-center font-medium ${
                    state.stats.consistentSavings 
                      ? 'bg-green-500' 
                      : 'bg-amber-500'
                  }`}>
                    {state.stats.consistentSavings 
                      ? '¡Felicidades! Has ganado +15 puntos por ahorros consistentes' 
                      : `Necesitas completar ${Math.ceil(6 * 0.8) - state.periods.filter(p => p.completed).length} períodos más para ganar +15 puntos`
                    }
                  </div>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const amount = (e.target as any).amount.value;
                    if (amount) {
                      addSaving(Number(amount));
                      (e.target as any).amount.value = '';
                    }
                  }}
                  className="flex gap-2"
                >
                  <Input
                    type="number"
                    name="amount"
                    placeholder="Monto a ahorrar"
                    className="flex-1"
                  />
                  <Button type="submit">Ahorrar</Button>
                </form>
              </div>
            </div>
          </Card>

          {state.savedAmount >= state.selectedGoal.amount && (
            <div className="text-center">
              <Button
                onClick={() => updateProgress.mutate()}
                size="lg"
                className="animate-pulse"
              >
                ¡Completar Meta!
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="w-full max-w-4xl mx-auto p-2 sm:p-6">
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
        {/* Botón Volver */}
        <button
          onClick={() => navigate('/games')}
          className="mb-4 flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Volver a Juegos</span>
        </button>

        {/* Header del juego */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 sm:mb-6 gap-2">
          <div className="flex items-center gap-2">
            <PiggyBank className="w-6 h-6 text-primary animate-pulse" />
            <h2 className="text-2xl font-bold">Metas de Ahorro</h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              <span className="text-lg font-semibold">Puntos: {state.score}</span>
            </div>
            <div className="flex items-center gap-2">
              <Timer className="w-5 h-5 text-blue-500" />
              <span className="text-lg font-semibold">
                Tiempo: {Math.floor(state.timeLeft / 60)}:{(state.timeLeft % 60).toString().padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>

        {/* Contenido del juego */}
        {content}
      </div>
    </div>
  );
};
